/**
 * Keep in sync with benchling/processes/processes_platform/feature_configs/feature_options.py
 */
import {MaybeCoreObject, DomainGraphInterface} from 'app-core/framework/CoreObjectConstants';
import {
  doesObjectHaveInterface,
  getObjectType,
  isCoreObject,
} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {getPlatformConfigForItemType} from 'app-core/framework/PlatformConfiguration';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {
  ProcessesFeature,
  RecipesFeature,
  AssaysFeature,
  AnyCapabilityFeature,
  Capability,
  CapabilityFeatureDefinition,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {getCapabilityFeatureFromStandardFeature} from 'auth/capabilities/getCapabilityFeatures';
import {FeatureDefinition} from 'auth/feature-enablement/FeatureConstants';
import {areAllFeaturesEnabled, isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {
  ProcessExecutionType,
  ProcessPlatformFeatureType,
  ProcessClientOrigin,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  SINGLETON_SCHEMA_FEATURE_CONFIG_OVERRIDES,
  SingletonProcessTaskSchemaName,
} from 'processes/processes-platform/SingletonSchemas';

export type FeatureConfigDict = {
  [feature in ProcessPlatformFeatureType]: boolean;
};

const DEFAULT_FEATURE_CONFIGS: FeatureConfigDict = {
  CAPILLARY_CREATE: false,
  CAPILLARY_UPDATE: false,
  CONFIG_MIGRATION: false,
  SCHEMA_CONFIG: false,
  WAREHOUSE_PER_SCHEMA_TABLE: false,
  WORKFLOWS_SEARCH: false,
  IS_STUDYABLE: false,
  IS_DIRECTLY_EXECUTABLE: false,
  IS_DIRECTLY_ARCHIVEABLE: false,
  TASK_NODE_EXTENSION: false,
  ARE_OUTPUTS_VISIBLE: false,
  ARE_SCHEMA_FIELDS_VISIBLE: false,
  CAN_CREATE_SCHEMAS_WITHOUT_ADMIN_PERMS: false,
  CAN_UPDATE_SCHEMAS_WITHOUT_ADMIN_PERMS: false,
  CAN_EDIT_ASSIGNEE: false,
  CAN_EDIT_RESPONSIBLE_PARTIES: false,
  CAN_EDIT_SCHEMA_FIELDS: false,
  CAN_EDIT_SCHEDULED_ON_DATE: false,
  CAN_EDIT_STATUS_FROM_UI: false,
  CAN_EDIT_STUDIES_METADATA: false,
  CAN_COPY_TASKS: false,
  CAN_CREATE_TASKS: false,
  CAN_EDIT_TASK_GROUPS: false,
  CAN_CREATE_TASK_GROUPS: false,
  CAN_MANUALLY_CREATE_OUTPUTS: false,
  CAN_CREATE_REQUEST_V2: false,
  CAN_CREATE_OUTPUTS_WHEN_TASK_IS_PENDING: false,
  CAN_COMPLETE_TASKS_WITH_NO_OUTPUTS: false,
};

const DEFAULT_WORKFLOWS_APP_FEATURE_CONFIGS: FeatureConfigDict = {
  CAPILLARY_CREATE: true,
  CAPILLARY_UPDATE: true,
  CONFIG_MIGRATION: true,
  SCHEMA_CONFIG: true,
  WAREHOUSE_PER_SCHEMA_TABLE: true,
  WORKFLOWS_SEARCH: true,
  IS_STUDYABLE: true,
  IS_DIRECTLY_EXECUTABLE: true,
  IS_DIRECTLY_ARCHIVEABLE: true,
  TASK_NODE_EXTENSION: false,
  ARE_OUTPUTS_VISIBLE: true,
  ARE_SCHEMA_FIELDS_VISIBLE: true,
  CAN_CREATE_SCHEMAS_WITHOUT_ADMIN_PERMS: false,
  CAN_UPDATE_SCHEMAS_WITHOUT_ADMIN_PERMS: false,
  CAN_EDIT_ASSIGNEE: true,
  CAN_EDIT_RESPONSIBLE_PARTIES: true,
  CAN_EDIT_SCHEMA_FIELDS: true,
  CAN_EDIT_SCHEDULED_ON_DATE: true,
  CAN_EDIT_STATUS_FROM_UI: true,
  CAN_EDIT_STUDIES_METADATA: true,
  CAN_COPY_TASKS: true,
  CAN_CREATE_TASKS: true,
  CAN_EDIT_TASK_GROUPS: true,
  CAN_CREATE_TASK_GROUPS: true,
  CAN_MANUALLY_CREATE_OUTPUTS: true,
  CAN_CREATE_REQUEST_V2: true,
  CAN_CREATE_OUTPUTS_WHEN_TASK_IS_PENDING: false,
  CAN_COMPLETE_TASKS_WITH_NO_OUTPUTS: false,
};

const DIRECT_EXECUTION_TYPE_TO_FEATURE_CONFIG_OVERRIDES: FeatureConfigDict =
  DEFAULT_WORKFLOWS_APP_FEATURE_CONFIGS;

const ENTRY_EXECUTION_TYPE_TO_FEATURE_CONFIG_OVERRIDES: FeatureConfigDict =
  DEFAULT_WORKFLOWS_APP_FEATURE_CONFIGS;

const FLOWCHART_EXECUTION_TYPE_TO_FEATURE_CONFIG_OVERRIDES: FeatureConfigDict = {
  ...DEFAULT_WORKFLOWS_APP_FEATURE_CONFIGS,
  CAN_EDIT_ASSIGNEE: false,
  CAN_EDIT_SCHEDULED_ON_DATE: false,
  IS_DIRECTLY_EXECUTABLE: false,
};

const PROCEDURE_EXECUTION_TYPE_TO_FEATURE_CONFIG_OVERRIDES: FeatureConfigDict = {
  ...DEFAULT_FEATURE_CONFIGS,
  IS_STUDYABLE: true,
  CAN_CREATE_SCHEMAS_WITHOUT_ADMIN_PERMS: true,
  CAN_UPDATE_SCHEMAS_WITHOUT_ADMIN_PERMS: true,
};

const PROCEDURE_METHOD_EXECUTION_TYPE_TO_FEATURE_CONFIG_OVERRIDES: FeatureConfigDict = {
  ...DEFAULT_FEATURE_CONFIGS,
  TASK_NODE_EXTENSION: true,
  CAN_EDIT_ASSIGNEE: true,
  CAN_EDIT_SCHEDULED_ON_DATE: true,
  CAN_EDIT_STUDIES_METADATA: true,
  IS_DIRECTLY_EXECUTABLE: true,
  IS_STUDYABLE: true,
  CAN_CREATE_SCHEMAS_WITHOUT_ADMIN_PERMS: true,
  CAN_UPDATE_SCHEMAS_WITHOUT_ADMIN_PERMS: true,
  CAN_CREATE_OUTPUTS_WHEN_TASK_IS_PENDING: true,
};

export const PROCEDURE_STEP_EXECUTION_TYPE_TO_FEATURE_CONFIG_OVERRIDES: FeatureConfigDict = {
  ...DEFAULT_FEATURE_CONFIGS,
  CAN_EDIT_ASSIGNEE: true,
  CAN_EDIT_SCHEDULED_ON_DATE: true,
  CAN_EDIT_STUDIES_METADATA: true,
  IS_DIRECTLY_EXECUTABLE: true,
  IS_STUDYABLE: true,
  CAN_CREATE_SCHEMAS_WITHOUT_ADMIN_PERMS: true,
  CAN_UPDATE_SCHEMAS_WITHOUT_ADMIN_PERMS: true,
};

// Mapping from a client origin to the capabilities required to access tasks of that client origin.
const CLIENT_ORIGIN_TO_CAPABILITIES = {
  [ProcessClientOrigin.PROCESSES]: [Capability.PROCESSES],
  [ProcessClientOrigin.ASSAYS]: [Capability.PROCESSES, Capability.ASSAYS],
  [ProcessClientOrigin.RECIPES]: [Capability.PROCESSES, Capability.RECIPES],
};

// Mapping from a platform feature to the capability required to access that feature.
const PLATFORM_FEATURE_TO_CAPABILITY: {
  [platformFeature in ProcessPlatformFeatureType]?: StandardFeature;
} = {
  IS_DIRECTLY_EXECUTABLE: StandardFeature.EDIT,
  CAN_EDIT_ASSIGNEE: StandardFeature.EDIT,
  CAN_EDIT_RESPONSIBLE_PARTIES: StandardFeature.EDIT,
  CAN_EDIT_SCHEMA_FIELDS: StandardFeature.EDIT_TAGS,
  CAN_EDIT_SCHEDULED_ON_DATE: StandardFeature.EDIT,
  CAN_EDIT_STATUS_FROM_UI: StandardFeature.EDIT,
  CAN_EDIT_STUDIES_METADATA: StandardFeature.EDIT,
  CAN_COPY_TASKS: StandardFeature.CREATE,
  CAN_CREATE_TASKS: StandardFeature.CREATE,
  WORKFLOWS_SEARCH: StandardFeature.DISCOVER,
};

// Mapping which transforms a standard feature for a specific execution type and client origin to a list of
// capabilities required to access that feature.
// Note that this is useful when a feature is gated by a capability that does not map to a StandardFeature.
// For example editing the assignee of a Workflows app task (clientOrigin = PROCESS, executionType = ENTRY)
// requires the PROCESSES_EDIT capability, which corresponds to StandardFeature.EDIT.
// However, editing the assignee of a Recipe task (clientOrigin = RECIPES, executionType = PROCEDURE_METHOD
// requires the RECIPES_EXECUTE_EXPERIMENT in addition to the PROCESSES_EDIT capability which does not
// correspond to a StandardFeature.
const EXECUTION_TYPE_AND_CLIENT_ORIGIN_TO_CAPABILITY_OVERRIDES: {
  [executionType in ProcessExecutionType]?: {
    [clientOrigin in ProcessClientOrigin]?: {
      [standardFeature in StandardFeature]?: Array<AnyCapabilityFeature>;
    };
  };
} = {
  [ProcessExecutionType.PROCEDURE]: {
    [ProcessClientOrigin.RECIPES]: {
      [StandardFeature.EDIT]: [RecipesFeature.RECIPES_EXECUTE_EXPERIMENT, ProcessesFeature.PROCESSES_EDIT],
    },
    [ProcessClientOrigin.ASSAYS]: {
      [StandardFeature.EDIT]: [AssaysFeature.ASSAYS_EXECUTE_EXPERIMENT, ProcessesFeature.PROCESSES_EDIT],
    },
  },
  [ProcessExecutionType.PROCEDURE_METHOD]: {
    [ProcessClientOrigin.RECIPES]: {
      [StandardFeature.EDIT]: [RecipesFeature.RECIPES_EXECUTE_EXPERIMENT, ProcessesFeature.PROCESSES_EDIT],
    },
    [ProcessClientOrigin.ASSAYS]: {
      [StandardFeature.EDIT]: [AssaysFeature.ASSAYS_EXECUTE_EXPERIMENT, ProcessesFeature.PROCESSES_EDIT],
    },
  },
  [ProcessExecutionType.PROCEDURE_STEP]: {
    [ProcessClientOrigin.RECIPES]: {
      [StandardFeature.EDIT]: [RecipesFeature.RECIPES_EXECUTE_EXPERIMENT, ProcessesFeature.PROCESSES_EDIT],
    },
    [ProcessClientOrigin.ASSAYS]: {
      [StandardFeature.EDIT]: [AssaysFeature.ASSAYS_EXECUTE_EXPERIMENT, ProcessesFeature.PROCESSES_EDIT],
    },
  },
};

function getProcessPlatformFeatureConfigsByExecutionType(): {
  [executionType in ProcessExecutionType]: FeatureConfigDict;
} {
  return {
    [ProcessExecutionType.ENTRY]: ENTRY_EXECUTION_TYPE_TO_FEATURE_CONFIG_OVERRIDES,
    [ProcessExecutionType.DIRECT]: DIRECT_EXECUTION_TYPE_TO_FEATURE_CONFIG_OVERRIDES,
    [ProcessExecutionType.FLOWCHART]: FLOWCHART_EXECUTION_TYPE_TO_FEATURE_CONFIG_OVERRIDES,
    [ProcessExecutionType.PROCEDURE_METHOD]: PROCEDURE_METHOD_EXECUTION_TYPE_TO_FEATURE_CONFIG_OVERRIDES,
    [ProcessExecutionType.PROCEDURE]: PROCEDURE_EXECUTION_TYPE_TO_FEATURE_CONFIG_OVERRIDES,
    [ProcessExecutionType.PROCEDURE_STEP]: PROCEDURE_STEP_EXECUTION_TYPE_TO_FEATURE_CONFIG_OVERRIDES,
  };
}

export function getProcessPlatformFeatureConfig(
  feature: ProcessPlatformFeatureType,
  executionType: ProcessExecutionType,
  schemaName?: string
): boolean {
  if (
    schemaName != null &&
    (Object.values(SingletonProcessTaskSchemaName) as Array<string>).includes(schemaName)
  ) {
    const singletonSchemaOverrides =
      SINGLETON_SCHEMA_FEATURE_CONFIG_OVERRIDES[schemaName as SingletonProcessTaskSchemaName];
    if (singletonSchemaOverrides.has(feature)) {
      return singletonSchemaOverrides.get(feature)!;
    }
  }

  const PROCESS_PLATFORM_FEATURE_CONFIGS_BY_EXECUTION_TYPE =
    getProcessPlatformFeatureConfigsByExecutionType();
  return PROCESS_PLATFORM_FEATURE_CONFIGS_BY_EXECUTION_TYPE[executionType][feature];
}

export function hasCapabilityForProcessPlatformFeature(
  feature: ProcessPlatformFeatureType,
  executionType: ProcessExecutionType,
  clientOrigin: ProcessClientOrigin
): boolean {
  const standardFeatureForProcessPlatformFeature = PLATFORM_FEATURE_TO_CAPABILITY[feature];
  if (standardFeatureForProcessPlatformFeature == null) {
    return true;
  }

  const capabilityOverride =
    EXECUTION_TYPE_AND_CLIENT_ORIGIN_TO_CAPABILITY_OVERRIDES[executionType]?.[clientOrigin]?.[
      standardFeatureForProcessPlatformFeature
    ] ?? null;

  if (capabilityOverride == null) {
    return CLIENT_ORIGIN_TO_CAPABILITIES[clientOrigin]?.every((capability) => {
      const capabilityFeature = getCapabilityFeatureFromStandardFeature(
        capability,
        standardFeatureForProcessPlatformFeature
      );
      return capabilityFeature != null
        ? isFeatureEnabled({feature: capabilityFeature} as CapabilityFeatureDefinition)
        : true;
    });
  }

  return areAllFeaturesEnabled(
    capabilityOverride.map((capability) => ({feature: capability}) as FeatureDefinition)
  );
}

/**
 * Determines whether a process platform feature is enabled and the user has the required capabilities to
 * access the feature.
 *
 * This function is useful in cases when we want to exhibit the same behavior if a feature is disabled for
 * the task type as a whole as we would if the user doesn't have permissions to access that feature.
 *
 * @param feature The process platform feature to check
 * @param executionType The execution type of the task
 * @param clientOrigin The client origin of the task
 * @returns Whether the feature is enabled and the user has the required capabilities to access the feature
 */
export function isProcessPlatformFeatureEnabledAndHasCapability(
  feature: ProcessPlatformFeatureType,
  executionType: ProcessExecutionType,
  clientOrigin: ProcessClientOrigin,
  schemaName: string
): boolean {
  return (
    getProcessPlatformFeatureConfig(feature, executionType, schemaName) &&
    hasCapabilityForProcessPlatformFeature(feature, executionType, clientOrigin)
  );
}

export function canEditSchemaFieldValues(item: MaybeCoreObject): boolean {
  return (
    isCoreObject(item) &&
    doesObjectHaveInterface(item, DomainGraphInterface.CAN_EDIT_WORKFLOW_SCHEMA_FIELD_VALUES) &&
    getPlatformConfigForItemType(
      DomainGraphInterface.CAN_EDIT_WORKFLOW_SCHEMA_FIELD_VALUES,
      getObjectType(item)
    )!.isPlatformCapabilityEnabledForItemInstance(item)
  );
}

// Keep in sync with benchling/processes/models/process_task_schema.py:ProcessTaskSchema.is_flowchart_type
export function isFlowchartType(executionType: ProcessExecutionType): boolean {
  return [ProcessExecutionType.FLOWCHART, ProcessExecutionType.PROCEDURE].includes(executionType);
}
